import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box, Stack, Typography, Card, CardContent, FormControl, InputLabel,
  Select, MenuItem, Paper, Divider, CircularProgress, Alert,
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';

const API = 'https://portal.peaksoftcon.com:8081/api';

function thisYear() {
  return new Date().getFullYear();
}

const WEEKDAY_TR = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];
const MONTHS_TR = [
  'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
];

function fmtTRDate(s) {
  // Backend returns "YYYY-MM-DD" — render as "5 Mayıs 2026 (Salı)".
  if (!s) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) return s;
  const d = new Date(+m[1], +m[2] - 1, +m[3]);
  return `${d.getDate()} ${MONTHS_TR[d.getMonth()]} ${d.getFullYear()} (${WEEKDAY_TR[d.getDay()]})`;
}

export default function ClientHolidays() {
  const [year, setYear] = useState(thisYear());
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`${API}/holidays`, { params: { year } });
      setHolidays(data || []);
    } catch (err) {
      setError('Tatiller yüklenemedi: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const yearOptions = [];
  for (let y = thisYear() - 1; y <= thisYear() + 2; y++) yearOptions.push(y);

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        mb={2}
      >
        <Typography variant="h4" sx={{ flex: { sm: 1 } }}>Resmi Tatiller</Typography>
        <FormControl size="small">
          <InputLabel id="hol-year">Yıl</InputLabel>
          <Select
            labelId="hol-year"
            label="Yıl"
            value={year}
            onChange={(e) => setYear(e.target.value)}
            sx={{ minWidth: 100 }}
          >
            {yearOptions.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
          </Select>
        </FormControl>
      </Stack>

      <Typography variant="caption" color="text.secondary" display="block" mb={2}>
        Bu listedeki tarihler aylık raporlamada haftaiçi olsa bile mesai dışı sayılır.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Stack direction="row" justifyContent="center" my={4}>
          <CircularProgress />
        </Stack>
      ) : holidays.length === 0 ? (
        <Paper sx={{ py: 6, textAlign: 'center' }}>
          <EventIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">{year} için tatil tanımlı değil.</Typography>
        </Paper>
      ) : (
        <Stack spacing={1.5}>
          {holidays.map((h) => (
            <Card key={h.id} variant="outlined">
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <EventIcon color="primary" sx={{ mt: 0.5 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {h.name}
                    </Typography>
                    <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
                      {fmtTRDate(h.date)}
                    </Typography>
                    {h.notes && (
                      <>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                          {h.notes}
                        </Typography>
                      </>
                    )}
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
    </Box>
  );
}
